import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";

import AccountMenu from "./AccountMenu";
import BrandLogo from "./BrandLogo";
import { getInitials } from "./account";

import { TOPBAR_HEIGHT } from "./layout";

export { TOPBAR_HEIGHT };

export interface TopBarProps {
  /** Hamburger: collapse/expand the sidebar (md+) or open the nav drawer (below md). */
  onMenuClick: () => void;
  /** Whether the navigation is currently expanded/open (for aria-expanded). */
  navExpanded: boolean;
  /** id of the navigation element the hamburger controls. */
  navId?: string;
}

/**
 * App header: hamburger, Care Solutions wordmark, "CSI Pay", and the account button
 * that opens the account menu. Sticks to the top while the page scrolls.
 */
export function TopBar({ onMenuClick, navExpanded, navId }: TopBarProps) {
  const { instance } = useMsal();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const name = instance.getActiveAccount()?.name || "User";
  const menuOpen = Boolean(anchorEl);

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        top: 0,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: `${TOPBAR_HEIGHT}px !important`,
          px: { xs: 2, sm: 4.5 },
          gap: 3.5,
        }}
      >
        <IconButton
          edge="start"
          onClick={onMenuClick}
          aria-label="Main menu"
          aria-expanded={navExpanded}
          aria-controls={navId}
          sx={{ color: "text.secondary", ml: 0 }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          component={RouterLink}
          to="/"
          aria-label="CSI Pay home"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            minWidth: 0,
            color: "text.primary",
            textDecoration: "none",
            borderRadius: 1,
            "&:focus-visible": {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          <BrandLogo height={22} />
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              my: "auto",
              height: 26,
              display: { xs: "none", sm: "block" },
            }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              display: { xs: "none", sm: "inline" },
            }}
          >
            CSI Pay
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />

        <ButtonBase
          id="account-button"
          onClick={(event: React.MouseEvent<HTMLElement>) =>
            setAnchorEl(event.currentTarget)
          }
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? "account-menu" : undefined}
          aria-label={`Account: ${name}`}
          sx={(theme) => ({
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 999,
            padding: "3px",
            [theme.breakpoints.up("sm")]: { padding: "3px 8px 3px 3px" },
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.8125rem",
            color: theme.palette.text.primary,
            maxWidth: 260,
            "&:hover": { backgroundColor: theme.palette.grey[50] },
          })}
        >
          <Avatar
            sx={{
              width: 26,
              height: 26,
              fontSize: "0.6875rem",
              fontWeight: 500,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            {getInitials(name === "User" ? "" : name)}
          </Avatar>
          <Box
            component="span"
            sx={{
              display: { xs: "none", sm: "inline" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Box>
          <KeyboardArrowDown
            aria-hidden
            sx={{
              fontSize: 18,
              color: "text.secondary",
              display: { xs: "none", sm: "block" },
            }}
          />
        </ButtonBase>

        <AccountMenu
          id="account-menu"
          buttonId="account-button"
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
        />
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
